import React, { useEffect, useMemo, useRef } from 'react';

// Simple TradingView Advanced Chart embed that swaps symbols on prop change
// and preserves the terminal-green dark theme vibe as much as TradingView allows.
// Note: TradingView widget is mostly themed via built-in dark/light. We keep
// the surrounding container styled by the app.

type Props = {
  baseSymbol: string; // e.g., "BTC", "ETH", "SOL", "HBAR", "PYTH"
  quote?: 'USDT' | 'USD';
  interval?: '1' | '3' | '5' | '15' | '30' | '60' | '120' | '240' | 'D' | 'W' | 'M';
  autosize?: boolean;
};

// Map our base symbols to TradingView symbols. Defaults to BINANCE:<SYMBOL><QUOTE> if known.
function toTradingViewSymbol(base: string, quote: string): string {
  const b = base.toUpperCase();
  const q = quote.toUpperCase();

  // Common direct mappings
  const direct: Record<string, string> = {
    BTC: `BINANCE:BTC${q}`,
    ETH: `BINANCE:ETH${q}`,
    SOL: `BINANCE:SOL${q}`,
    HBAR: `BINANCE:HBAR${q}`,
    PYTH: `BINANCE:PYTH${q}`,
  };

  if (direct[b]) return direct[b];
  // Fallback to Crypto composite
  return `CRYPTO:${b}${q === 'USDT' ? 'USD' : q}`;
}

declare global {
  interface Window {
    TradingView?: any;
  }
}

export const TradingViewChart: React.FC<Props> = ({ baseSymbol, quote = 'USDT', interval = '60', autosize = true }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerId = useMemo(() => `tv_chart_${Math.random().toString(36).slice(2)}`, []);

  const tvSymbol = useMemo(() => toTradingViewSymbol(baseSymbol, quote), [baseSymbol, quote]);

  useEffect(() => {
    // Inject script if needed
    const ensureScript = () =>
      new Promise<void>((resolve) => {
        if (window.TradingView) return resolve();
        const scriptId = 'tradingview-widget-script';
        if (document.getElementById(scriptId)) {
          // Wait a tick to allow it to load
          const iv = setInterval(() => {
            if (window.TradingView) {
              clearInterval(iv);
              resolve();
            }
          }, 50);
          return;
        }
        const s = document.createElement('script');
        s.id = scriptId;
        s.type = 'text/javascript';
        s.async = true;
        s.src = 'https://s3.tradingview.com/tv.js';
        s.onload = () => resolve();
        document.head.appendChild(s);
      });

    let destroyed = false;

    const init = async () => {
      await ensureScript();
      if (destroyed) return;

      // Clear previous widget content
      const el = document.getElementById(containerId);
      if (!el) return;
      el.innerHTML = '';

      // Create the chart
      try {
        // eslint-disable-next-line no-new
        new window.TradingView.widget({
          width: autosize ? '100%' : undefined,
          height: autosize ? '100%' : 520,
          autosize,
          symbol: tvSymbol,
          interval, // 60 -> 1h
          timezone: 'Etc/UTC',
          theme: 'dark',
          style: '1', // candles
          locale: 'en',
          enable_publishing: false,
          hide_top_toolbar: false,
          hide_legend: false,
          withdateranges: true,
          allow_symbol_change: false,
          container_id: containerId,
          // Keep the widget clean; TV controls will still show but blend with dark theme
          studies: [],
          // The following keys are honored by TV; some styling is limited.
          // backgroundColor isn't officially part of widget options, but we keep our
          // surrounding container styled to terminal palette.
        });
      } catch (e) {
        console.error('TradingView widget init failed', e);
      }
    };

    init();

    return () => {
      destroyed = true;
      const el = document.getElementById(containerId);
      if (el) el.innerHTML = '';
    };
  }, [containerId, tvSymbol, autosize, interval]);

  return (
    <div className="w-full h-full bg-[#0d0d2a] rounded-lg overflow-hidden border border-green-500/30">
      <div id={containerId} ref={containerRef} className="w-full h-[540px] md:h-[620px]" />
    </div>
  );
};
